export type CvLink = { label: string; href: string };

export type CvExperience = {
  role: string;
  period: string;
  org: string;
  orgHref?: string;
  meta: string;
  paragraphs: string[];
};

export type CvProject = {
  name: string;
  period?: string;
  org?: string;
  paragraphs: string[];
  links?: CvLink[];
};

export type Cv = {
  name: string;
  currentRole: string;
  location: string;
  email: string;
  links: CvLink[];
  summary: string;
  skills: { label: string; text: string; italicTail?: string }[];
  education: { school: string; program: string; note?: string }[];
  experience: CvExperience[];
  freeTime: string[];
  publicSpeaking: { title: string; body: string; italicTail?: string }[];
  press: { title: string; body: string; href?: string }[];
  sideProjects: CvProject[];
  footer: string;
};

export const cv: Cv = {
  name: "Ondřej Bárta",
  currentRole: "Currently Engineering Manager at Glassnode",
  location: "Prague, Czechia",
  email: "email@ondrejbarta.com",
  links: [
    { label: "github.com/bartaxyz", href: "https://github.com/bartaxyz" },
    {
      label: "linkedin.com/in/bartaxyz",
      href: "https://linkedin.com/in/bartaxyz",
    },
  ],
  summary:
    "Engineering leader with over a decade building software and 5+ years leading teams. At the intersection of full-stack engineering, data, product, and engineering leadership.",
  skills: [
    {
      label: "Leadership",
      text: "Hiring, staffing, coaching, performance evaluation & career growth, engineering strategy, roadmap & OKRs",
    },
    {
      label: "Languages",
      text: "TypeScript (JavaScript), Go, Python, SQL, Bash",
      italicTail: "Released projects also using Swift, C#, Kotlin, and others",
    },
    {
      label: "Full-stack & data",
      text: "Node.js (NestJS), Go (gRPC, Pub/Sub), REST, GraphQL, PostgreSQL, Redis, BigQuery, dbt, Airflow, data pipelines, WebSockets, React, Next.js, React Native (Expo/EAS), SSR & Core Web Vitals",
    },
    {
      label: "Infrastructure",
      text: "GCP, Kubernetes, Terraform, Docker, Cloudflare, CI/CD, observability (Grafana, Prometheus)",
    },
    {
      label: "AI",
      text: "Agent / LLM tooling, MCP servers, Vercel AI & OpenAI SDKs, evals (Braintrust and local), local models",
    },
  ],
  education: [
    {
      school:
        "Faculty of Electrical Engineering, Czech Technical University in Prague",
      program: "Software Engineering (SIT)",
    },
    { school: "SPŠ a VOŠ Písek", program: "Computer Science" },
  ],
  experience: [
    {
      role: "Engineering Manager",
      period: "2022 – Present",
      org: "Glassnode",
      orgHref: "https://glassnode.com",
      meta: "digital asset market intelligence · Remote (Berlin, Copenhagen, Barcelona, Prague)",
      paragraphs: [
        "As an engineering manager, I grew from a small mobile team, to a greenfield portfolio product, to data delivery, managing **up to 12 engineers across two teams**. That meant coaching, setting engineering strategy, and holding the bar on engineering practices, while staying hands on across frontend, backend, and BI (anywhere from 20% to 80% of my time, depending on the needs of the team). I worked on introducing an **empowered engineering culture** on a new full-stack team, with **trunk-based development** and small, continuously shipped changes. Later, we spread the model more broadly. I helped engineers grow into bigger roles, three to senior and two moving into data engineering, and kept optimizing the teams to ship faster and more reliably.",
        "My teams at Glassnode owned the data delivery mechanisms, data visualization, information architecture, billing, [public APIs](https://docs.glassnode.com), permissions, BI, and content pages. A server-side-rendering migration enabled new products (e.g. [Market Compass](https://glassnode.com/products/studio/market-compass)) to be **released in a single day**. An **in-house AI agent platform** lets non-technical people **safely ship to production with zero side effects** (limited access & robust harness). I owned **direct data delivery for institutional clients**, co-developed [Bitcoin Vector](https://glassnode.com/pricing/vector) with SwissBlock and built tooling for Coinbase, and opened a **self-serve purchase channel** now driving ~15% of all B2B revenue.",
        "Other notable work includes a **charting rebuild** enabling data teams to ship new metrics into [Studio](https://studio.glassnode.com) in under a day, payments migrations, automation of BI processes, global search, external AI chat and internal AI tooling, experimental data delivery mechanisms (MCP & CLI), direct engineering support for institutional customers, and performance and UX improvements.",
        "Currently, I'm **designing Studio's information-architecture overhaul end-to-end, a ground-up refactor to an entity-first data model** that lets the product access data from any point of view (by exchange, network, treasury, or any other facet).",
      ],
    },
    {
      role: "Sr. Full Stack Engineer to Engineering Manager",
      period: "2017 – 2022",
      org: "Bitwala / Nuri",
      orgHref: "https://bitwala.com",
      meta: "fintech, digital banking & investing · Berlin & Remote (Montreal)",
      paragraphs: [
        "Bitwala offered a German bank account with the ability to invest in and use crypto assets with ease. I joined early and built its foundation across the whole stack. I grew alongside the company as it **scaled to over 500,000 customers**.",
        "I worked on cross-platform apps & backend on the bank's regulated products, built on top of Solaris. **Bank-grade crypto custody**, **card issuing**, account management, and the crypto investment and automated crypto savings account. The BaFin-regulated context meant correctness, compliance, and reconciliation were critical and shaped much of the work.",
        "As I moved into management, I **led the neobank team** which was the banking side of the product, and tech-led company-wide efforts such as the **Bitwala to Nuri rebrand** (6 people across 3 teams)."
      ],
    },
    {
      role: "Engineering Consultant",
      period: "2019 – Present",
      org: "Kilta",
      orgHref: "https://www.linkedin.com/company/kilta/",
      meta: "design & engineering consultancy · Remote, Prague",
      paragraphs: [
        "Full-stack eng. and advisory work on client projects (**XTND**, **Klarity**, **Enehano**, and many others).",
      ],
    },
    {
      role: "Software Engineer",
      period: "2015 – 2017",
      org: "Jackova Továrna",
      meta: "on-demand apparel e-shop · Prague",
      paragraphs: [
        "Co-owned and built an on-demand custom-apparel e-shop from scratch as **one of two founding engineers**. The system **auto-generated** ads and **over 200,000 pixel-perfect assets**, sending each ready-to-print design straight to the printing provider.",
      ],
    },
  ],
  freeTime: [
    "Since I enjoy the act of creating, and I enjoy programming, portion of my free time is also dedicated to learning new things and creating things that I find either useful or beautiful.",
    "As part of my free time, I'm still involved in [Kilta](https://www.linkedin.com/company/kilta/) and I'm a member of [TechSquat](https://techsquat.com/), non-profit co-work & co-live community with strong passion for technology, design and innovation.",
  ],
  publicSpeaking: [
    {
      title: "Summer School for CzechITas",
      body: "No-preparation (improvised) live coding workshop about Internet of Things & JavaScript for 80 women trying to break into IT industry",
      italicTail:
        "With co-teacher Pavel Král who built hardware while I was coding",
    },
    {
      title: "Blockchain workshop for Element Insurance EG",
      body: "Introducing blockchain to 66 people from Element Insurance EG to help them building blockchain insurance products",
    },
  ],
  press: [
    {
      title:
        "Evolution Exchange Denmark Podcast #183: Working With Remote And Multi-Cultural Teams",
      body: "Podcast episode about working with remote and multi-cultural teams",
      href: "https://open.spotify.com/episode/5cCRY1Ow2skD1zBeAi9N5j",
    },
    {
      title:
        "Love + blockchain = Lovechain, the most enduring way of declaring love (in Czech)",
      body: "Magazine post about Lovechain project",
    },
    {
      title: "Trendsletter #9: Low-Code (in Czech)",
      body: "Curator of newsletter issue #9 of Kilta's Trendsletter about low-code future for almost 200 readers",
    },
  ],
  sideProjects: [
    {
      name: "Lensta",
      period: "2022 – 2023",
      paragraphs: [
        "A visual social dapp built on Lens protocol. It received **multiple grants, and direct support from the Aave team**. As part of this project, we've also launched a very successful verification campaign directly on the Lens protocol that generated worthwhile revenue.",
        "Discontinued due to lack of time and capacity to manage the project.",
        "Native apps published on Apple App Store & Google Play Store, and a web app. Used GCP on the backend.",
      ],
      links: [
        {
          label: "Lensta on X",
          href: "https://twitter.com/lenstaxyz",
        },
      ],
    },
    {
      name: "TabPilot",
      period: "2026 – Present",
      paragraphs: [
        "A Safari tab-search extension for macOS, **featured by Apple in the App Store**.",
      ],
      links: [
        {
          label: "TabPilot for Safari",
          href: "/tabpilot",
        },
      ],
    },
    {
      name: "GitPT",
      period: "2025 – Present",
      paragraphs: [
        "An open-source git CLI alias/hook that writes your commit messages and PR descriptions with an LLM, built for **on-device models** like Apple's Foundation Models, Ollama, and LM Studio. Also works with OpenAI, Anthropic, etc.",
      ],
      links: [
        {
          label: "GitPT on GitHub",
          href: "https://github.com/bartaxyz/GitPT",
        },
      ],
    },
    {
      name: "Swimming Water Quality Denmark",
      period: "2025 - Present",
      paragraphs: [
        "An iOS app to check **water quality and temperature** before going out for a swim in Denmark.",
      ],
      links: [
        {
          label: "Swimming Water Quality Denmark",
          href: "/denmark-swimming",
        },
      ],
    },
    {
      name: "Thamira",
      period: "2021 – 2023",
      paragraphs: [
        "Mobile strategy & action game created in **Unity (C#)**. Contains unique custom controls for magic casting mechanics, tiling mechanics for game map (islands), custom pathfinding, AI and a working multiplayer mode.",
      ],
    },
    {
      name: "ondrejbarta.com",
      period: "2024 – Present",
      paragraphs: [
        'My personal site as a **live systems playground**: a "build pulse" visualizing my real integration-event activity across every project since 2013 (via a local dbt pipeline), plus live signals from my phone streamed in near-real-time. Also serves this machine-readable CV.',
      ],
      links: [{ label: "Personal site", href: "https://ondrejbarta.com" }],
    },
  ],
  footer:
    "More recent information about my current projects, my involvement with non-profit groups & my side projects on https://ondrejbarta.com",
};
